import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { duration } from "@formant/core/model/duration";
import { IAnalyticsModuleParameters } from "@formant/core/model/IAnalyticsModuleParameters";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IComment } from "@formant/core/model/IComment";
import { ICustomModuleParameters } from "@formant/core/model/ICustomModuleParameters";
import { IDevice } from "@formant/core/model/IDevice";
import { IEvent } from "@formant/core/model/IEvent";
import { IFilter } from "@formant/core/model/IFilter";
import { ILayoutModule } from "@formant/core/model/ILayoutModule";
import { IPartialViewConfiguration } from "@formant/core/model/IPartialViewConfiguration";
import { IShare } from "@formant/core/model/IShare";
import { IViewConfiguration } from "@formant/core/model/IViewConfiguration";
import { ModuleMode } from "@formant/core/model/ModuleMode";

import { customModuleStore, observeStore } from "../../stores";
import { AnalyticsModule } from "./AnalyticsModule";
import { BatteryModule } from "./BatteryModule";
import { BitsetModule } from "./BitsetModule";
import { ControlModule } from "./ControlModule";
import { CustomVisualizationModule } from "./CustomVisualizationModule";
import { FileModule } from "./FileModule";
import * as styles from "./GridModule.scss";
import { HealthModule } from "./HealthModule";
import { IBaseModuleProps } from "./IBaseModuleProps";
import { ImageModule } from "./ImageModule";
import { JsonModule } from "./JsonModule";
import { LocalizationModule } from "./LocalizationModule";
import { LocationModule } from "./LocationModule";
import { Module } from "./Module";
import { NumericModule } from "./NumericModule";
import { NumericSetModule } from "./NumericSetModule";
import { PlaceHolderModule } from "./PlaceHolderModule";
import { PointCloudModule } from "./PointCloudModule";
import { ScrollTo } from "./ScrollTo";
import { TextModule } from "./TextModule";
import { TransformTreeModule } from "./TransformTreeModule";
import { VideoModule } from "./VideoModule";

export interface IGridModuleProps {
    streamName: string;
    streamNames: string[];
    streamType: AddableModuleType;
    hideDevice: boolean;
    filter: IFilter;
    configuration: IViewConfiguration[];
    focused: boolean;
    device?: IDevice;
    onDemand?: boolean;
    activeEvent?: IEvent;
    fullscreen?: boolean;
    editingLayout?: boolean;
    noGrid?: boolean;
    teleop?: boolean;
    topTeleop?: boolean;
    moduleMode: ModuleMode;
    onFocus?: (stream: [string, AddableModuleType?]) => void;
    onBlur?: () => void;
    onToggleFullscreen?: (stream?: [string, AddableModuleType?]) => void;
    onClose?: () => void;
    onSelectTime?: (time: Date) => void;
    onSeek?: (time: Date) => void;
    onChangeConfiguration?: (value: IPartialViewConfiguration) => void;
    onComment?: (comment: Partial<IComment>) => void;
    onAnnotate?: (annotation: Partial<IAnnotation>) => void;
    onDemandData?: (streamName: string) => void;
    share?: IShare;
    forceShow?: boolean;
    layoutModule?: ILayoutModule;
    onConfigureModule?: (moduleId: string) => void;
    onRemoveModule?: (moduleId: string) => void;
}

const modules = new Set<GridModule>();

setInterval(() => modules.forEach(_ => _.checkVisible()), duration.second);

@observer
export class GridModule extends Component<IGridModuleProps> {
    @observable
    public visible: boolean = false;
    @observable
    public hovering: boolean = false;
    private element?: HTMLElement;
    @observable
    public customModuleConfigurationSchemaUrl?: string;
    @observable
    public customModuleConfigurationId?: string;
    @observable
    public analyticsModuleConfigurationId?: string;

    public async componentDidMount() {
        const { layoutModule, editingLayout } = this.props;
        modules.add(this);
        if (!editingLayout) {
            if (layoutModule?.configuration?.moduleType === "custom") {
                const params = layoutModule.configuration.parameters;
                if (params) {
                    const customModuleConfigurationParams =
                        params as ICustomModuleParameters;
                    const moduleDefinitionId =
                        customModuleConfigurationParams.customModuleDefinitionId;
                    this.customModuleConfigurationId =
                        customModuleConfigurationParams.customModuleConfigurationId;
                    if (moduleDefinitionId) {
                        const moduleDefinition =
                            await customModuleStore.fetch(moduleDefinitionId);
                        if (moduleDefinition) {
                            this.customModuleConfigurationSchemaUrl =
                                moduleDefinition.configurationSchemaUrl ??
                                undefined;
                        }
                    }
                }
            } else if (
                layoutModule?.configuration?.moduleType === "analytics"
            ) {
                const params = layoutModule.configuration.parameters;
                if (params) {
                    const analyticsParams =
                        params as IAnalyticsModuleParameters;
                    this.analyticsModuleConfigurationId =
                        analyticsParams.AnalyticsModuleDefinitionId;
                }
            }
        }
    }

    public componentWillUnmount() {
        modules.delete(this);
    }

    public checkVisible() {
        const { streamName, layoutModule } = this.props;
        const isCustomVisualization =
            streamName.startsWith("custom-module:") ||
            layoutModule?.configuration?.customVisualizationUrl !== undefined;

        const isAnalytics = streamName.startsWith("analytics-module:");

        if (isCustomVisualization || isAnalytics) {
            this.visible = true;
            return;
        }

        const { element } = this;
        if (!element) {
            return;
        }
        const module = element.parentElement;
        if (!module) {
            return;
        }

        const container = module.closest('[class^="content"]');
        if (!container) {
            return; // expects "contentDSAFKLJASKLFJALKSDFJLKASFJLKASFDJ" in parent class string or else modules will never be visible
        }

        const { top, right, bottom, left } = module.getBoundingClientRect();
        const { innerHeight, innerWidth } = window;

        this.visible =
            bottom > 0 && top < innerHeight && right > 0 && left < innerWidth;
    }

    public render() {
        const {
            streamName,
            streamType,
            noGrid,
            hideDevice,
            share,
            onClose,
            forceShow,
            layoutModule,
            onConfigureModule,
            onRemoveModule,
            topTeleop,
        } = this.props;
        const {
            visible,
            hovering,
            props,
            onFocus,
            onBlur,
            onToggleFullscreen,
        } = this;
        const { arrangingModules } = observeStore;

        const event = props.activeEvent;
        const activeEvent =
            event && event.streamName === streamName ? event : undefined;

        const moduleProps: IBaseModuleProps = {
            ...props,
            hovering,
            activeEvent,
            onFocus,
            onBlur,
            onToggleFullscreen,
            onClose,
            hideDevice,
            share,
            onConfigureModule,
            onRemoveModule,
            nonInteractable: arrangingModules,
            teleop: props.teleop ?? false,
            moduleMode: props.moduleMode,
            streamType,
        };

        if (!props.editingLayout && !this.customModuleConfigurationSchemaUrl) {
            moduleProps.onConfigureModule = undefined;
        }

        const isCustomVisualization =
            streamName.startsWith("custom-module:") ||
            layoutModule?.configuration?.customVisualizationUrl !== undefined;

        const isAnalytics =
            layoutModule?.configuration?.moduleType === "analytics";

        const isPlaceholderModule = streamName.startsWith(
            "placeholder-module:",
        );

        const isRegularModule =
            !isCustomVisualization && !isAnalytics && !isPlaceholderModule;

        const visibleConfig =
            moduleProps.layoutModule?.configuration?.visible ?? true;

        return visibleConfig || props.editingLayout ? (
            <div
                className={classnames(
                    styles["grid-module"],
                    noGrid && styles["no-grid"],
                    hovering && styles.hovering,
                    topTeleop && styles["top-teleop"],
                )}
                ref={_ => (this.element = _ || undefined)}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
            >
                {activeEvent?.streamName === streamName && (
                    <ScrollTo key={activeEvent?.id} />
                )}
                {(forceShow || visible) && isCustomVisualization && (
                    <CustomVisualizationModule
                        {...moduleProps}
                        moduleSchemaUrl={
                            this.customModuleConfigurationSchemaUrl
                        }
                        moduleConfigurationId={this.customModuleConfigurationId}
                    />
                )}
                {(forceShow || visible) && isAnalytics && (
                    <AnalyticsModule
                        {...moduleProps}
                        moduleConfigurationId={
                            this.analyticsModuleConfigurationId
                        }
                    />
                )}
                {(forceShow || visible) && isPlaceholderModule && (
                    <PlaceHolderModule {...moduleProps} />
                )}
                {(forceShow || visible) && isRegularModule && (
                    <>
                        {streamType === "numeric" && (
                            <NumericModule {...moduleProps} />
                        )}
                        {streamType === "image" && (
                            <ImageModule {...moduleProps} />
                        )}
                        {streamType === "point cloud" && (
                            <PointCloudModule {...moduleProps} />
                        )}
                        {streamType === "text" && (
                            <TextModule {...moduleProps} />
                        )}
                        {streamType === "json" && (
                            <JsonModule {...moduleProps} />
                        )}
                        {streamType === "location" && (
                            <LocationModule {...moduleProps} />
                        )}
                        {streamType === "file" && (
                            <FileModule {...moduleProps} />
                        )}
                        {streamType === "localization" && (
                            <LocalizationModule {...moduleProps} />
                        )}
                        {streamType === "health" && (
                            <HealthModule {...moduleProps} />
                        )}
                        {streamType === "bitset" && (
                            <BitsetModule {...moduleProps} />
                        )}
                        {streamType === "transform tree" && (
                            <TransformTreeModule {...moduleProps} />
                        )}
                        {streamType === "battery" && (
                            <BatteryModule {...moduleProps} />
                        )}
                        {(streamType === "controls" ||
                            streamType === "joystick-controls" ||
                            streamType === "button-controls") && (
                            <ControlModule {...moduleProps} />
                        )}
                        {streamType === "numeric set" && (
                            <NumericSetModule {...moduleProps} />
                        )}
                        {streamType === "video" && (
                            <VideoModule {...moduleProps} />
                        )}
                        {streamType === undefined && (
                            <Module {...moduleProps} empty={true} />
                        )}
                    </>
                )}
                {!forceShow && !visible && (
                    <Module {...moduleProps} type={streamType} loading={true} />
                )}
            </div>
        ) : null;
    }

    private onFocus = () => {
        const { streamName, streamType, onFocus } = this.props;
        if (onFocus) {
            if (streamType !== "custom" && streamType !== "analytics") {
                onFocus([streamName, streamType]);
            } else {
                onFocus([streamName, undefined]);
            }
        }
    };

    private onBlur = () => {
        const { onBlur } = this.props;
        if (onBlur) {
            onBlur();
        }
    };

    private onToggleFullscreen = () => {
        const { streamName, streamType, onToggleFullscreen } = this.props;
        if (onToggleFullscreen) {
            onToggleFullscreen([streamName, streamType]);
        }
    };

    private onMouseEnter = () => {
        if (this.props.editingLayout) {
            return;
        }
        this.hovering = true;
    };

    private onMouseLeave = () => {
        this.hovering = false;
    };
}
